import { Mail } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, AlertColor, AlertTitle, Box, Stack } from "@mui/material";
import { BaseState } from "common/models";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import {
  getActiveOrganisation,
  IdentityState,
  Organisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { Role, UserMembership } from "data/models";
import { fetchSubscription } from "data/queries";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { formatTimestamp } from "utils";
import { makeApiRequestPostToUrl, makeApiRequestToUrl } from "utils/api-client";
import { resolveIdentityId } from "utils/identity";
import { object, string } from "yup";
import { InvitationItem, MemberInvitationModal } from "./invite-members";
import {
  InvitationList,
  InvitationListState,
} from "./organisation-invitation-list";

export interface OrganisationInviteState extends BaseState {
  userEmail: OrganisationInvitation;
}

export type OrganisationInvitation = {
  userEmail: string;
  organisationId: string;
  invitationStatus: string;
};

interface AlertState {
  message: string;
  severity: AlertColor;
}

export const invitationValidator = object({
  userEmail: string()
    .email("Enter a valid email")
    .required("Email is required"),
});

export type InvitationResponse = {
  userExisted: boolean;
};

const fetchOrganisationInvitations = async (
  currentOrganisation: Organisation | false,
  setInvitationsListState: Dispatch<SetStateAction<InvitationListState>>,
  identityState: IdentityState,
  setIdentityState: Dispatch<SetStateAction<IdentityState>>
) => {
  setInvitationsListState((prevState) => {
    return {
      ...prevState,
      message: "",
      isError: false,
      isLoading: true,
    };
  });
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?organisationId=${
        currentOrganisation
          ? encodeURIComponent(currentOrganisation.organisationId)
          : ""
      }`,
      "GET"
    );
    if (response.status !== 200) {
      setInvitationsListState((prevState) => {
        return {
          ...prevState,
          message:
            "An error occurred while fetching the organisation invitations",
          isError: true,
          isLoading: false,
        };
      });
    } else {
      const invitationList: UserMembership[] = await response.json();
      setInvitationsListState((prevState) => {
        return {
          ...prevState,
          message: "",
          invitationList: invitationList,
          isError: false,
          isLoading: false,
        };
      });
    }
  } catch (e) {
    setInvitationsListState((prevState) => {
      return {
        ...prevState,
        message: "",
        isError: true,
        isLoading: false,
      };
    });
  }
};

const submitInvitation = async (
  invitationItems: InvitationItem[],
  currentOrganisation: Organisation | false,
  setState: Dispatch<SetStateAction<OrganisationInviteState>>,
  invitationsListState: InvitationListState,
  setInvitationsListState: Dispatch<SetStateAction<InvitationListState>>,
  identityState: IdentityState,
  setIdentityState: Dispatch<SetStateAction<IdentityState>>,
  setInvitationModalOpen: Dispatch<SetStateAction<boolean>>,
  setAlertStateList: Dispatch<SetStateAction<AlertState[]>>,
  setShowAlert: Dispatch<SetStateAction<boolean>>
) => {
  const alertStateList: AlertState[] = [];

  if (!currentOrganisation) {
    return;
  }
  setState((prevState) => {
    return {
      ...prevState,
      message: "",
      isLoading: true,
      isError: false,
    };
  });
  await Promise.all(
    invitationItems.map((invitationItem) =>
      makeApiRequestPostToUrl(
        `${import.meta.env.VITE_DELIVERY_API_URL}`,
        "/v2/organisation/membership",
        "POST",
        {
          userEmail: invitationItem.email,
          organisationId: currentOrganisation.organisationId,
          invitationStatus: "PENDING",
          role: invitationItem.role,
        }
      )
    )
  ).then((results) => {
    results.forEach(async (r, i) => {
      const res = await r.json();
      let message = res?.messages?.join(". ");
      const invitationItem = invitationItems[i];
      let severity: AlertColor = "info";
      if (r.status !== 200) {
        severity = "error";
      } else {
        message = res.userExisted
          ? `Invitation to ${invitationItem.email} has been sent. Invited user is already registered with Geoscape.`
          : `Invitation to ${invitationItem.email} has been sent. Invited user does not exist in Geoscape. They may need to sign up.`;
        severity = "info";
      }
      alertStateList.push({
        message: message,
        severity: severity,
      });
    });
  });

  const currentInvitations = invitationsListState.invitationList;
  fetchOrganisationInvitations(
    currentOrganisation,
    setInvitationsListState,
    identityState,
    setIdentityState
  );
  setInvitationsListState({
    message: "",
    invitationList: currentInvitations,
    isError: false,
    isLoading: false,
  });
  setState((prevState) => {
    return {
      ...prevState,
      isError: false,
      isLoading: false,
      message: "",
    };
  });
  setInvitationModalOpen(false);
  setShowAlert(true);
  setAlertStateList(alertStateList);
};

const openModal = (
  setState: Dispatch<SetStateAction<OrganisationInviteState>>,
  setInvitationModalOpen: Dispatch<SetStateAction<boolean>>
) => {
  setState((prevState) => {
    return {
      ...prevState,
      message: "Invitations have been sent",
      userEmail: {
        userEmail: "",
        organisationId: "",
        invitationStatus: "PENDING",
      },
      isError: false,
      isLoading: false,
    };
  });
  setInvitationModalOpen(true);
};

export const OrganisationMembers = () => {
  const initialOrganisationInviteState = {
    message: "",
    userEmail: {
      userEmail: "",
      organisationId: "",
      invitationStatus: "PENDING",
    },
    isLoading: false,
    isError: false,
  };
  const initialInvitationListState: InvitationListState = {
    message: "",
    invitationList: [],
    isError: false,
    isLoading: true,
  };
  const [state, setState] = useState<OrganisationInviteState>(
    initialOrganisationInviteState
  );
  const [invitationsListState, setInvitationsListState] =
    useState<InvitationListState>(initialInvitationListState);
  const [identityState, setIdentityState] = useIdentity();
  const currentOrganisation: Organisation | false =
    getActiveOrganisation(identityState);

  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [alertStateList, setAlertStateList] = useState<AlertState[]>([
    { message: "", severity: "info" },
  ]);
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    if (showAlert) {
      const timeId = setTimeout(() => {
        setShowAlert(false);
      }, 5000);

      return () => {
        clearTimeout(timeId);
      };
    }
  }, [showAlert]);

  useEffect(() => {
    fetchOrganisationInvitations(
      currentOrganisation,
      setInvitationsListState,
      identityState,
      setIdentityState
    );
  }, [identityState, setIdentityState]);

  const identityId = resolveIdentityId(identityState);

  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription()
  );

  const activeSub = subscriptionQuery.data?.activeSubscription;
  const hasScheduledUpgradeOrDowngrade = activeSub?.nextSub != null;
  const hasScheduledCancellation = activeSub?.cancelAtPeriodEnd === true;

  const futurePlanPermissionKey = subscriptionQuery.data?.activeSubscription
    ?.nextSub?.plan.product_key
    ? [subscriptionQuery.data.activeSubscription?.nextSub.plan.product_key]
    : [];

  const {
    isPermitted: futurePlanIsPermitted,
    isPermissionsSuccess: isFuturePlanPermissionsSuccess,
  } = usePermissions({
    permissionSets: futurePlanPermissionKey,
  });

  const onAdminInitiatedTrial =
    subscriptionQuery.data?.activeSubscription?.trialType == "admin";

  const isInvitationsListLoaded =
    !invitationsListState.isLoading && invitationsListState;

  const isSubscriptionReady = subscriptionQuery.isSuccess;

  const isFuturePlanReady = isFuturePlanPermissionsSuccess;

  const hasBlockingCollaboration =
    hasScheduledCancellation ||
    (hasScheduledUpgradeOrDowngrade &&
      !futurePlanIsPermitted([PermissionsKeys.membersCreate]));

  const hasOwner = getOwner(invitationsListState?.invitationList);

  const showCollaborationEndingAlert =
    isInvitationsListLoaded &&
    isSubscriptionReady &&
    !onAdminInitiatedTrial &&
    isFuturePlanReady &&
    hasBlockingCollaboration &&
    hasOwner;

  return (
    <Box>
      <MemberInvitationModal
        isOpen={invitationModalOpen}
        isLoading={state.isLoading}
        isError={state.isError}
        handleClose={() => setInvitationModalOpen(false)}
        handleSubmit={(itemList: InvitationItem[]) =>
          submitInvitation(
            itemList,
            currentOrganisation,
            setState,
            invitationsListState,
            setInvitationsListState,
            identityState,
            setIdentityState,
            setInvitationModalOpen,
            setAlertStateList,
            setShowAlert
          )
        }
        orgName={(currentOrganisation as Organisation).name}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          position: "relative",
          top: "-50px",
        }}
      >
        <PermissionTooltipWrapper
          permissionKeys={[PermissionsKeys.membersCreate]}
        >
          <LoadingButton
            loading={invitationsListState.isLoading}
            onClick={() => openModal(setState, setInvitationModalOpen)}
            disabled={!currentOrganisation || state.isLoading}
            startIcon={<Mail />}
            variant="contained"
          >
            Invite Members
          </LoadingButton>
        </PermissionTooltipWrapper>
      </Box>

      <PermissionsWrapper
        keys={[PermissionsKeys.membersCreate]}
        messages={{
          [PermissionsReasons.ROLE_DENY]: <></>,
          [PermissionsReasons.UPGRADE_REQUIRED]: (
            <Alert
              variant="outlined"
              severity="info"
              action={
                <UpgradeSubscriptionButton
                  variant="text"
                  color="inherit"
                  style={{
                    justifyContent: "flex-end",
                    textAlign: "right",
                    width: 140,
                  }}
                  text="Review Plans"
                />
              }
            >
              <AlertTitle>Ready to Grow Your Team?</AlertTitle>
              Unlock the ability to invite members to your organisation by
              upgrading to our Team or Pro plan.
            </Alert>
          ),
        }}
      >
        <PermissionsWrapper
          keys={[PermissionsKeys.rolesUpdate]}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <Alert
                variant="outlined"
                severity="info"
                action={
                  <UpgradeSubscriptionButton
                    variant="text"
                    color="inherit"
                    style={{
                      justifyContent: "flex-end",
                      textAlign: "right",
                      width: 140,
                    }}
                    text="Review Plans"
                  />
                }
              >
                <AlertTitle>Unlock Roles & Permissions with Pro</AlertTitle>
                Roles and Permissions allow you to define specific access levels
                for your team members. This feature is included in our Pro plan.
              </Alert>
            ),
          }}
        ></PermissionsWrapper>
      </PermissionsWrapper>
      {showCollaborationEndingAlert && (
        <>
          <Alert
            variant="outlined"
            severity="warning"
            sx={{ marginTop: "16px" }}
            action={
              <UpgradeSubscriptionButton
                variant="text"
                color="inherit"
                style={{
                  justifyContent: "flex-end",
                  textAlign: "left",
                  width: 140,
                }}
                text="Review Plans"
              />
            }
          >
            <AlertTitle>
              Upcoming Change: Collaboration Ending{" "}
              {formatTimestamp(
                (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                  1000
              )}
            </AlertTitle>
            Please note: Your plan is scheduled to downgrade on{" "}
            {formatTimestamp(
              (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                1000
            )}
            . This will result in the loss of your organisation's collaboration
            features, and only the owner will be able to use Geoscape Hub.
          </Alert>
        </>
      )}

      {showAlert && renderAlerts(alertStateList)}
      <PermissionsWrapper
        keys={[PermissionsKeys.membersRead]}
        messages={{
          [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
        }}
      >
        <InvitationList
          invitationsState={invitationsListState}
          setInvitationsState={setInvitationsListState}
        />
      </PermissionsWrapper>
    </Box>
  );
};

const getOwner = (invitationList: UserMembership[]): string | null => {
  const findByRole = (role: Role): string | undefined =>
    invitationList.find((invitation) => invitation.role == role)?.userEmail;

  return findByRole(Role.OWNER) || null;
};

const renderAlerts = (alertStateList: AlertState[]) => {
  return (
    <Stack sx={{ width: "100%" }} spacing={2}>
      {alertStateList
        .filter((m) => m.message)
        .map((alert: AlertState, index) => (
          <Alert key={index} variant="filled" severity={alert.severity}>
            {alert.message}
          </Alert>
        ))}
    </Stack>
  );
};
